import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, Input, Message, Radio, Spin } from '@arco-design/web-react';
import { IconArrowRight } from '@arco-design/web-react/icon';

import { useCaptcha } from '@/components/CaptchaInput';

import styles from '../index.module.less';
import { registerEmail, registerPhone, sendEmailCode, sendSmsCode } from '../service';

const FormItem = Form.Item;

const primaryBtnStyle: React.CSSProperties = {
  fontWeight: 600,
  borderRadius: '4px',
  marginTop: 16,
};

type LoginConfig = Record<string, boolean | undefined>;

export default function Register({
  loginConfig,
  registerTypes,
  setActiveTab,
}: {
  loginConfig: LoginConfig;
  registerTypes: string[];
  setActiveTab: (tab: string) => void;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [countdown, setCountdown] = useState(0);

  useCaptcha({
    refreshInterval: 60000,
    onValidChange: (isValid) => {
      console.log('验证码验证结果:', isValid);
    },
  });

  useEffect(() => {
    if (registerTypes.includes('phoneRegister')) {
      form.setFieldsValue({ registerType: 'phone' });
    } else if (registerTypes.includes('emailRegister')) {
      form.setFieldsValue({ registerType: 'email' });
    }
  }, [registerTypes, form]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleBackToLogin = useCallback(() => {
    setActiveTab('login');
    form.resetFields();
    setCountdown(0);
  }, [form, setActiveTab]);

  const handleRegister = useCallback(async () => {
    try {
      const values = await form.validate();
      if (values.registerType === 'phone') {
        await registerPhone({
          displayName: values.displayName,
          phone: values.phone,
          code: values.code,
          codesecret: values.codesecret,
          password: values.password,
        });
      } else if (values.registerType === 'email') {
        await registerEmail({
          displayName: values.displayName,
          email: values.email,
          code: values.code,
          codesecret: values.codesecret,
          password: values.password,
        });
      }
      Message.success(t('login-page:registration.successful.please.log.in'));
      handleBackToLogin();
    } catch {}
  }, [form, handleBackToLogin, t]);

  const handleSendCode = useCallback(async () => {
    try {
      const values = await form.validate(['phone', 'email', 'registerType']);
      if (values.registerType === 'phone') {
        const res = await sendSmsCode({ phone: values.phone });
        form.setFieldValue('codesecret', res.secret);
      } else {
        const res = await sendEmailCode({ email: values.email });
        form.setFieldValue('codesecret', res.secret);
      }
      Message.success(t('login-page:verification.code.sent.successfully'));
      setCountdown(60);
    } catch {
      Message.error(t('login-page:failed.to.send.verification.code'));
      setCountdown(0);
    }
  }, [form, t]);

  return (
    <Spin className={styles.content} style={{ padding: '0 1rem' }} block loading={false}>
      <Form form={form} layout="vertical" autoComplete="off">
        {registerTypes.length > 1 ? (
          <FormItem field="registerType" initialValue="phone">
            <Radio.Group type="button" defaultValue="phone" style={{ width: '100%', display: 'flex' }}>
              {loginConfig?.phoneRegister && (
                <Radio value="phone" style={{ flex: 1 }}>
                  {t('common:mobile.number')}
                </Radio>
              )}
              {loginConfig?.emailRegister && (
                <Radio value="email" style={{ flex: 1 }}>
                  {t('common:email')}
                </Radio>
              )}
            </Radio.Group>
          </FormItem>
        ) : (
          <FormItem field="registerType" hidden>
            <Input />
          </FormItem>
        )}

        <FormItem
          label={t('common:user.nickname')}
          field="displayName"
          rules={[{ required: true, match: /\S/, message: t('common:input.placeholder.name', { name: t('common:user.nickname') }) }]}
        >
          <Input
            placeholder={t('common:input.placeholder.name', { name: t('common:user.nickname') })}
            maxLength={63}
            showWordLimit
            autoComplete="nickname"
          />
        </FormItem>

        <FormItem shouldUpdate noStyle>
          {(values) =>
            values?.registerType === 'phone' ? (
              <FormItem
                label={t('common:mobile.number')}
                field="phone"
                rules={[
                  { required: true, message: t('common:please.enter.mobile.number') },
                  {
                    validator: (value, callback) => {
                      if (!/^1[3-9]\d{9}$/.test(value)) {
                        callback(t('common:please.enter.a.valid.phone.number'));
                      }
                      callback();
                    },
                  },
                ]}
              >
                <Input placeholder={t('common:please.enter.mobile.number')} autoComplete="off" />
              </FormItem>
            ) : (
              <FormItem label={t('common:email')} field="email" rules={[{ required: true, message: t('common:please.enter.email') }]}>
                <Input placeholder={t('common:please.enter.email')} autoComplete="off" />
              </FormItem>
            )
          }
        </FormItem>

        <FormItem
          label={t('login-page:verification.code')}
          field="code"
          rules={[{ required: true, message: t('common:please.enter.the.verification.code') }]}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Input
              placeholder={t('common:please.enter.the.verification.code')}
              maxLength={11}
              autoComplete="off"
              onChange={(v) => form.setFieldValue('code', v)}
            />
            <Button type="primary" loading={false} onClick={handleSendCode} disabled={countdown > 0}>
              {countdown > 0 ? t('common:resend.countdown', { seconds: countdown }) : t('login-page:send.verification.code')}
            </Button>
          </div>
        </FormItem>
        <FormItem field="codesecret" hidden>
          <Input />
        </FormItem>

        <Form.Item label={t('common:password')} field="password" rules={[{ required: true, message: t('common:please.enter.password') }]}>
          <Input.Password placeholder={t('common:please.enter.password')} autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          label={t('login-page:confirm.password')}
          field="passwordConfirm"
          rules={[
            { required: true, message: t('login-page:please.enter.password.again') },
            {
              validator: (value, callback) => {
                const password = form.getFieldValue('password');
                if (value && password && value !== password) {
                  callback(t('login-page:the.two.entered.passwords.do.not.match'));
                } else {
                  callback();
                }
              },
            },
          ]}
          validateTrigger={['onChange', 'onBlur']}
        >
          <Input.Password placeholder={t('login-page:please.enter.password.again')} autoComplete="new-password" />
        </Form.Item>

        <div style={{ textAlign: 'center' }}>
          <Button long type="primary" style={primaryBtnStyle} onClick={handleRegister}>
            {t('login-page:register')}
            <IconArrowRight />
          </Button>
        </div>
      </Form>
    </Spin>
  );
}
